// AI-CONTEXT: See docs/features/supertenant-dashboard-cards.md
import formatEuro from '../formatEuro'

// The five platform-stat KPI tiles on the Deccos management dashboard. Keys
// are DashboardItems Types (see staticCardRegistry); fields match the
// PlatformStats/current doc written nightly by computePlatformStats.
export const PLATFORM_STAT_CARDS = {
    'platform-projects': { label: 'Actieve projecten', field: 'ActiveProjects', format: 'number' },
    'platform-team': { label: 'Teamleden totaal', field: 'TeamMembers', format: 'number' },
    'platform-personas': { label: 'Deelnemers totaal', field: 'Personas', format: 'number' },
    'platform-households': { label: 'Huishoudens totaal', field: 'Households', format: 'number' },
    'platform-value': { label: 'Maatschappelijke waarde totaal', field: 'MonetaryValue', format: 'euro' },
}

/**
 * Value formatter for one card type — euro tiles and count tiles differ, so
 * every consumer of a platform-stat value formats through this.
 *
 * @param {string} type - DashboardItems Type ('platform-projects', ...)
 * @returns {(value: number) => string}
 */
export const platformStatFormatter = (type) => {
    const config = PLATFORM_STAT_CARDS[type]
    if (!config) return (value) => String(value)
    if (config.format === 'euro') return formatEuro
    return (value) => new Intl.NumberFormat('nl-NL').format(value)
}

// Het platformtotaal Maatschappelijke waarde telt de stromen die de nachtelijke
// job kan berekenen. De methodiek kent er drie; de materialen-keten bestaat
// alleen client-side en is server-side nog niet gespiegeld. Het statsdocument
// draagt daarom `MonetaryValueStreams`, en die lijst bepaalt of er een
// voorbehoud bij het getal hoort.
//
// Het voorbehoud heft zichzelf op: zodra de job materialen meetelt, staat dat in
// de lijst en verdwijnt de regel. Spiegelbeeld van
// functions/platformStats/monetaryValueStreams.js — die twee horen bij elkaar.
// AI-CONTEXT: See docs/plans/ondersteuningsmodule-code-integratie.md (T4b)
const MATERIALS_STREAM = 'materials'
const INCOMPLETE_NOTE = 'Zonder materialen en coachadviezen'

/**
 * @param {object|null} stats
 * @returns {boolean} Een ontbrekend of onbruikbaar veld geldt als onvolledig —
 *   een oud statsdocument mag nooit als volledig lezen.
 */
const monetaryValueIsComplete = (stats) => {
    const streams = stats ? stats.MonetaryValueStreams : null
    return Array.isArray(streams) && streams.includes(MATERIALS_STREAM)
}

/**
 * Resolves the label + formatted value for one platform-stat card type.
 * `value` is null while the stats doc hasn't loaded (or lacks the field) —
 * the card shows a placeholder, never a fake 0. `note` carries a voorbehoud
 * when the figure is knowingly incomplete.
 *
 * @param {string} type - DashboardItems Type ('platform-projects', ...)
 * @param {object | null} stats - PlatformStats/current doc data
 * @returns {{ label: string, value: string | null, note: string | null } | null} null for unknown types
 */
const platformStatDisplay = (type, stats) => {
    const config = PLATFORM_STAT_CARDS[type]
    if (!config) return null

    const raw = stats ? stats[config.field] : undefined
    if (!Number.isFinite(raw)) return { label: config.label, value: null, note: null }

    // Alleen bij een getoond bedrag: een voorbehoud bij een streepje verklaart
    // niets en leest als een storing.
    const note = type === 'platform-value' && !monetaryValueIsComplete(stats)
        ? INCOMPLETE_NOTE
        : null

    return { label: config.label, value: platformStatFormatter(type)(raw), note }
}

export default platformStatDisplay
